/**
 * The blocking pre-paint theme script: before the first paint it puts the stored theme on
 * <html>, so a dark-theme user never sees a light flash while the app loads. ThemeProvider takes
 * over once React runs. The logic matches ThemeProvider: the stored choice, else the provider's
 * default; only "system" follows the OS.
 *
 * Vite apps paste the script into index.html (`humain-ui init` prints it). Server-rendered apps
 * (Next.js) render <ThemeScript /> from lib/providers/theme-script in the root layout's <head>.
 *
 * Plain TypeScript with no React, so a server component can import it in Next.js, and the
 * humain-ui build can run it to print the exact script.
 */

export type Theme = 'dark' | 'light' | 'system';

/** The key the pre-paint script reads. Changing it here without changing index.html gives a light flash on every load for anybody on dark. */
export const THEME_STORAGE_KEY = 'gox.theme';

export function themeScript(storageKey: string = THEME_STORAGE_KEY, defaultTheme: Theme = 'light'): string {
	// JSON.stringify quotes the values; escaping `<` keeps a stray `</script>` from ending the tag.
	const key = JSON.stringify(storageKey).replace(/</g, '\\u003c');
	const fallback = JSON.stringify(defaultTheme).replace(/</g, '\\u003c');
	return `(function(){try{var s=localStorage.getItem(${key})||${fallback};var d=s==='dark'||(s==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);var r=document.documentElement;r.dataset.theme=d?'dark':'light';r.classList.toggle('dark',d);r.classList.toggle('light',!d);}catch(e){document.documentElement.dataset.theme='light';}})();`;
}
